import AppConfig from "@/AppConfig";
import {
  buildRhiGenerateQuoteKey,
  buildRhiQueryKey,
  RHI_QUERY_KEYS,
} from "@/constants/queryKeys";
import { IGetQuotesRequest, IPurchaseIntentPayload } from "@/types";
import { decodeAuthToken, employeeExperienceAxiosInstance, getUserInfo } from "@/utils";
import employeeExperienceV1AxiosInstance from "@/utils/axios/employeeExperienceV1AxiosInstance";
import { getPlatformHeader } from "@/utils/deviceDetails";

// Extended interface for quote requests with riders
interface IExtendedQuotesRequest extends IGetQuotesRequest {
  riderIds?: Array<string>;
  planIds?: Array<string>;
}

const EKYC_REQUEST_TIMEOUT_MS = 125000;

export interface CreateOtpPayload {
  phoneNumber: string;
  aadharNumber: string;
  email: string;
  gender: string;
  nameAsPerAadhaar: string;
  dateOfBirth: string;
}

export interface VerifyOtpPayload {
  otp: string;
}

interface EkycMutationArgs<TPayload> {
  payload: TPayload;
  signal?: AbortSignal;
}

export class QuoteApiError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'QuoteApiError';
    this.code = code;
  }
}

export interface IProps {
  name: string;
  phone?: string;
  email?: string;
  productId?: string;
  query?: string;
  source?: string;
  isOffline?: boolean;
  info?: Record<string, unknown>;
  callbackRequested?: boolean;
  callbackMethod?: string;
  pincode?: string;
}

export const createLeadPayload = (props: IProps): IProps => {
  return {
    name: props.name,
    phone: props.phone,
    email: props.email,
    productId: AppConfig.RETAIL_HEALTH_INSURANCE_PRODUCT_ID,
    query: props.query || '',
    source: props.source || '',
    isOffline: props.isOffline || false,
    info: props.info,
    callbackMethod: props.callbackMethod,
    callbackRequested: props.callbackRequested,
    pincode: props.pincode
  };
}

export const createLeadQuery = () => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.CREATE_LEAD),
    mutationFn: async (requestBody: IProps) => {
      const url = `crm/leads`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);

      return response.data.data;
    },
  };
};

export const getRHILeads = () => {
  return {
    queryKey: buildRhiQueryKey(RHI_QUERY_KEYS.GET_RHI_LEADS),
    queryFn: async () => {
      const url = `crm/products/${AppConfig.RETAIL_HEALTH_INSURANCE_PRODUCT_ID}/leads?page=1&pageSize=10`;
      const response = await employeeExperienceV1AxiosInstance.get(url);

      return response.data;
    },
  };
};

export const updateLead = () => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.UPDATE_LEAD),
    mutationFn: async (leadId: string) => {
      const url = `crm/leads/${leadId}`;
      const response = await employeeExperienceV1AxiosInstance.patch(url, { callbackRequested: true });
      return response.data;
    },
  };
};

export const getConfig = (
) => {
  return {
    queryKey: buildRhiQueryKey(RHI_QUERY_KEYS.GET_CONFIG),
    queryFn: async () => {
      const url = `/rhi/config`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data;
    },
  };
};


export const generateQuote = (leadId: string, sumInsured: number, includeEssentialRiders: boolean, options?: {
  retry?: number;
  retryDelay?: number;
  throwOnError?: boolean;
}, riders?: Array<string>, planIds?: Array<string>, signal?: AbortSignal) => {
  return {
    queryKey: buildRhiGenerateQuoteKey(
      leadId,
      sumInsured,
      includeEssentialRiders,
      riders,
      planIds,
    ),
    queryFn: async () => {
      const requestBody: IExtendedQuotesRequest = {
        leadId,
        sumInsured,
        includeEssentialRiders
      };

      // Add riders and planIds if provided
      if (riders) {
        requestBody.riderIds = riders;
      }
      if (planIds && planIds.length > 0) {
        requestBody.planIds = planIds;
      }

      const url = `/rhi/generateQuote`;
      const response = await employeeExperienceAxiosInstance.post(url, requestBody, {
        signal,
      });
      const plans = response.data.data.plans;
      const errors = response.data.errors;

      if (errors?.length > 0 && (!plans || plans.length === 0)) {
        const firstError = errors[0] || {};
        throw new QuoteApiError(
          firstError.code || 'UNKNOWN',
          firstError.message || 'Failed to generate quote',
        );
      }

      return plans;
    },
    enabled: !!leadId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: options?.retry ?? 2, // Default 2 retries
    retryDelay: options?.retryDelay ?? 1000, // Default 1 second delay
    throwOnError: options?.throwOnError ?? true, // Default throw on error
  };
};

export const generatePurchaseIntent = (quoteId: string) => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.GENERATE_PURCHASE_INTENT, quoteId),
    mutationFn: async (requestBody: IPurchaseIntentPayload) => {
      const url = `/rhi/quote/${quoteId}/show-interest`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody, {
        headers: { platform: getPlatformHeader() },
      });
      return response.data.data;
    },
  };
};


export const getMedicalQuestions = (quoteId: string) => {
  return {
    queryKey: buildRhiQueryKey(RHI_QUERY_KEYS.GET_MEDICAL_QUESTIONS, quoteId),
    queryFn: async () => {
      const url = `/rhi/quotes/${quoteId}/medical-questionaires`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data.data;
    },
  };
}

export const submitMedicalQuestions = (quoteId: string) => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.SUBMIT_MEDICAL_QUESTIONS, quoteId),
    mutationFn: async (requestBody: unknown) => {
      const url = `/rhi/quotes/${quoteId}/medical-questionaires`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody, {
        headers: { platform: getPlatformHeader() },
      });
      return response.data;
    },
  };
}

export const createOtp = (quoteId: string) => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.CREATE_OTP, quoteId),
    mutationFn: async ({
      payload,
      signal,
    }: EkycMutationArgs<CreateOtpPayload>) => {
      const url = `/rhi/quote/${quoteId}/ekyc/initiate`;
      const response = await employeeExperienceV1AxiosInstance.post(url, payload, {
        timeout: EKYC_REQUEST_TIMEOUT_MS,
        signal,
        headers: { platform: getPlatformHeader() },
      });
      return response.data;
    },
  };
};

export const verifyOtp = (quoteId: string) => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.VERIFY_OTP, quoteId),
    mutationFn: async ({
      payload,
      signal,
    }: EkycMutationArgs<VerifyOtpPayload>) => {
      const url = `/rhi/quote/${quoteId}/ekyc/submit`;
      const response = await employeeExperienceV1AxiosInstance.post(url, payload, {
        timeout: EKYC_REQUEST_TIMEOUT_MS,
        signal,
        headers: { platform: getPlatformHeader() },
      });
      return response.data.data;
    },
  };
};

export const addNominee = (quoteId: string) => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.ADD_NOMINEE, quoteId),
    mutationFn: async (requestBody: unknown) => {
      const url = `/rhi/quotes/${quoteId}/nominee-details`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);
      return response.data.data;
    },
  };
};

export const addBankDetails = (quoteId: string) => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.ADD_BANK_DETAILS, quoteId),
    mutationFn: async (requestBody: unknown) => {
      const url = `/rhi/quotes/${quoteId}/bank-details`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);
      return response.data.data;
    },
  };
};

export const getProposal = (proposalId: string) => {
  return {
    queryKey: buildRhiQueryKey(RHI_QUERY_KEYS.GET_PROPOSAL, proposalId),
    queryFn: async () => {
      const url = `rhi/proposals/${proposalId}/checkout-link`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data;
    },
  };
};

export const getPolicyStatus = (requestedD2cUserId?: string, forceCheck?: boolean) => {
  const userInfo = getUserInfo();
  const tokenInfo = decodeAuthToken();
  const d2cUserId = requestedD2cUserId || userInfo?.d2cUserId || tokenInfo?.d2cUserId;
  return {
    queryKey: buildRhiQueryKey(RHI_QUERY_KEYS.GET_POLICY_STATUS, d2cUserId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (forceCheck) params.set('forceCheck', 'true');
      const query = params.size > 0 ? `?${params.toString()}` : '';
      const response = await employeeExperienceV1AxiosInstance.get(`/rhi/policy-status/${d2cUserId}${query}`);
      return response.data;
    },
  };
};

export const paymentCallback = () => {
  return {
    mutationKey: buildRhiQueryKey(RHI_QUERY_KEYS.PAYMENT_CALLBACK),
    mutationFn: async (transactionId: string) => {
      const url = `/rhi/payment-callback/`;
      const response = await employeeExperienceV1AxiosInstance.post(
        url,
        {
          transactionId,
        },
        {
          headers: { platform: getPlatformHeader() },
        },
      );
      return response.data;
    },
  };
};
